import React, { useState, useEffect } from 'react';
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { showSuccessAlert, showDeleteConfirmation, showItemExistsError, showItemAlreadyUsed } from '../../../../core/alert/sweetAlertConfig';

interface FormErrors {
  vehicleGearTypeName: string;
  selectedVehicleGearType: string;
}

interface VehicleGearType {
  id: number;
  gearTypeName: string;
}

interface VehicleGearTypeData {
  status: string;
  data: VehicleGearType[];
  statusDescription?: string;
}

const AddVehicleGearType: React.FC = () => {
  const [vehicleGearTypeData, setVehicleGearTypeData] = useState<VehicleGearTypeData | null>(null);
  const [isVehicleGearTypeLoading, setIsVehicleGearTypeLoading] = useState<boolean>(true);
  const [selectedVehicleGearTypeId, setSelectedVehicleGearTypeId] = useState<string>("");
  const [newVehicleGearTypeName, setNewVehicleGearTypeName] = useState<string>("");
  const [formErrors, setFormErrors] = useState<FormErrors>({
    vehicleGearTypeName: "",
    selectedVehicleGearType: "",
  });
  const [openFail, setOpenFail] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("Operation Failed");

  const handleCloseFail = () => setOpenFail(false);

  // Fetch vehicle gear types on component mount
  useEffect(() => {
    fetchVehicleGearTypes();
  }, []);

  const fetchVehicleGearTypes = () => {
    setIsVehicleGearTypeLoading(true);
    apiDefinitions.getAllVehicleGearTypes()
      .then((response: { data: VehicleGearTypeData }) => {
        if (response.data.status === "S1000" && response.data.data) {
          setVehicleGearTypeData(response.data);
        } else {
          setErrorMessage(response.data.statusDescription || "Failed to load vehicle gear types");
          setOpenFail(true);
        }
        setIsVehicleGearTypeLoading(false);
      })
      .catch((error: any) => {
        setErrorMessage(error.response?.data?.statusDescription || "Failed to load vehicle gear types: Server error");
        setOpenFail(true);
        setIsVehicleGearTypeLoading(false);
      });
  };

  const validateVehicleGearTypeForm = (name: string): boolean => {
    const errors: FormErrors = { vehicleGearTypeName: "", selectedVehicleGearType: "" };
    let isValid = true;

    if (!name.trim()) {
      errors.vehicleGearTypeName = "Vehicle gear type name is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateSelectedVehicleGearType = (): boolean => {
    const errors: FormErrors = { vehicleGearTypeName: "", selectedVehicleGearType: "" };
    let isValid = true;

    if (!selectedVehicleGearTypeId) {
      errors.selectedVehicleGearType = "Please select a vehicle gear type";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAddVehicleGearTypeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateVehicleGearTypeForm(newVehicleGearTypeName)) return;

    apiDefinitions.createVehicleGearType({ gearTypeName: newVehicleGearTypeName })
      .then((response: { data: VehicleGearTypeData }) => {
        if (response.data.status === "S1000") {
          showSuccessAlert("Vehicle gear type added successfully");
          setNewVehicleGearTypeName("");
          fetchVehicleGearTypes();
        } else if (response.data.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A vehicle gear type with this name already exists.");
        } else {
          setErrorMessage(response.data.statusDescription || "Vehicle gear type creation failed");
          setOpenFail(true);
        }
      })
      .catch((error: any) => {
        if (error.response?.data?.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A vehicle gear type with this name already exists.");
        } else {
          setErrorMessage(error.response?.data?.statusDescription || "Failed to create vehicle gear type: Server error");
          setOpenFail(true);
        }
      });
  };

  const handleDeleteVehicleGearType = () => {
    if (!validateSelectedVehicleGearType()) return;

    showDeleteConfirmation(() => {
      apiDefinitions.deleteVehicleGearType(parseInt(selectedVehicleGearTypeId))
        .then((response: { data: VehicleGearTypeData }) => {
          if (response.data.status === "S1000") {
            setSelectedVehicleGearTypeId("");
            fetchVehicleGearTypes();
          } else if (response.data.statusDescription?.toLowerCase().includes("already used")) {
            showItemAlreadyUsed("Sorry, this gear type is already in use and cannot be deleted.");
          } else {
           showItemAlreadyUsed("Sorry, this gear type is already in use and cannot be deleted.");
          }
        })
        .catch((error: any) => {
          if (error.response?.data?.statusDescription?.toLowerCase().includes("already used")) {
            showItemAlreadyUsed("Sorry, this gear type is already in use and cannot be deleted.");
          } else {
            showItemAlreadyUsed("Sorry, this gear type is already in use and cannot be deleted.");
          }
        });
    });
  };

  const handleVehicleGearTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedVehicleGearTypeId(e.target.value);
    setFormErrors((prev) => ({ ...prev, selectedVehicleGearType: "" }));
  };

  return (
    <>
      <div className="card">
        <div className="card-body pb-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="fs-20 mb-0">Add Vehicle Gear Type</h4>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row" style={{ gap: '30px' }}>
                <div className="col-md-5">
                  <div className="mb-4">
                    <label className="col-form-label mb-2"><h5>Vehicle Gear Type</h5></label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '300px', padding: '10px 15px' }}
                        value={newVehicleGearTypeName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehicleGearTypeName(e.target.value)}
                        placeholder="Enter vehicle gear type name"
                      />
                      <button
                        type="button"
                        className="btn btn-success ms-3"
                        data-bs-toggle="modal"
                        data-bs-target="#vehicle-gear-type-modal"
                        style={{ padding: '10px 12px', whiteSpace: 'nowrap', display: 'inline-block' }}
                      >
                        Add Gear Type
                      </button>
                    </div>
                    {formErrors.vehicleGearTypeName && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleGearTypeName}</div>
                    )}
                    <select
                      className="form-select mt-3"
                      value={selectedVehicleGearTypeId}
                      onChange={handleVehicleGearTypeChange}
                      style={{ width: '300px', padding: '10px' }}
                    >
                      <option value="">Select Vehicle Gear Type</option>
                      {isVehicleGearTypeLoading ? (
                        <option disabled>Loading...</option>
                      ) : (
                        vehicleGearTypeData?.data?.map((vehicleGearType: VehicleGearType) => (
                          <option key={vehicleGearType.id} value={vehicleGearType.id}>{vehicleGearType.gearTypeName}</option>
                        ))
                      )}
                    </select>
                    {formErrors.selectedVehicleGearType && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.selectedVehicleGearType}</div>
                    )}
                    <div className="d-flex gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleDeleteVehicleGearType}
                        style={{ padding: '8px 16px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Vehicle Gear Type Modal */}
      <div className="modal fade" id="vehicle-gear-type-modal" tabIndex={-1} aria-labelledby="add-vehicle-gear-type" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="add-vehicle-gear-type">Add New Vehicle Gear Type</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddVehicleGearTypeSubmit}>
                <div className="mb-3">
                  <label htmlFor="newVehicleGearTypeName" className="form-label">Vehicle Gear Type Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newVehicleGearTypeName"
                    value={newVehicleGearTypeName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehicleGearTypeName(e.target.value)}
                    placeholder="Enter vehicle gear type name (e.g., Automatic)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.vehicleGearTypeName && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleGearTypeName}</div>
                  )}
                </div>
                <button type="submit" className="btn btn-success" data-bs-dismiss="modal" style={{ padding: '10px 20px' }}>
                  Add Vehicle Gear Type
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Error Notification */}
      {openFail && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
          style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100 }}
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseFail}
            aria-label="Close"
          ></button>
        </div>
      )}
    </>
  );
};

export default AddVehicleGearType;